import React, { useCallback, useEffect, useMemo, useState } from "react";

import { RegistrationFormCmp, RegistrationFormProps } from "./types";
import {
  useHandleRadioButtonChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { REGISTRATION_VALIDATION_SCHEMA } from "./validation";
import {
  initialRegistrationForm,
  registerUser,
  RegistrationFormValues,
} from "services/auth";
import { TextField } from "ui/components/TextField/TextField";
import { PasswordField } from "ui/components/TextField/PasswordField";
import { Loader } from "ui/components/Loader";
import { Link } from "react-router-dom";
import { LoginPage } from "../../../LoginPage";
import { RadioButton } from "ui/components/RadioButton";
import { RadioButtonOption } from "ui/components/RadioButton/types";
import { Button } from "ui/components/Button/Button";

import { TenantType } from "services/api/tenants/types";
import { initialTenantTypes } from "services/api/tenants/consts";
import { RegistrationIntro } from "../RegistrationIntro";

const RegistrationForm: RegistrationFormCmp = (
  props: RegistrationFormProps
) => {
  const { afterRegistration } = props;

  const [formValues, setFormValues] = useState<RegistrationFormValues>(
    initialRegistrationForm
  );

  const [selectedTenantType, setSelectedTenantType] = useState<TenantType>(
    initialTenantTypes[0]
  );
  const [errors, setErrors] = useState<Errors>({});
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setFormValues({ ...formValues, tenantType: initialTenantTypes[0].id });
  }, []);

  const handleFormSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();

      const isValid = validateYup(
        formValues,
        REGISTRATION_VALIDATION_SCHEMA,
        setErrors
      );

      if (!isValid || !isPasswordValid) {
        return;
      }

      try {
        await registerUser(formValues);

        afterRegistration();
      } catch {
        // Ignore error
      }
    },
    [afterRegistration, formValues, isPasswordValid]
  );

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleRadioButtonChange = useHandleRadioButtonChange(
    setFormValues,
    formValues
  );

  const handlePasswordChange = useCallback(
    (
      ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      passwordValid: boolean
    ) => {
      const value = ev.target.value ? ev.target.value : null;

      setFormValues((old) => ({ ...old, password: value }));
      setIsPasswordValid(passwordValid);
    },
    []
  );

  const handleTenantChange = useCallback(
    (name: string, item: RadioButtonOption) => {
      var tenantType = initialTenantTypes.find((i) => i.id === item.value);
      if (tenantType) {
        setSelectedTenantType(tenantType);
        setFormValues({ ...formValues, [name]: item.value });
        setErrors({});
      }
    },
    []
  );

  const tenantTypeItems: RadioButtonOption[] = useMemo(() => {
    return initialTenantTypes.map((item) => {
      return {
        label: item.name,
        value: item.id,
      };
    });
  }, [initialTenantTypes]);

  return (
    <>
      <div className="flex h-screen w-screen">
        <RegistrationIntro />
        <div className="w-full lg:w-1/2 flex justify-center items-center h-full">
          <div className="w-[400px]">
            <div className="text-center mb-4 text-[#344054] mb-3">
              <h1 className="text-2xl mb-1">Registracija</h1>
              <p className="text-base">Novi ste korisnik? Unesite podatke za registraciju</p>
            </div>
            <form onSubmit={handleFormSubmit} noValidate>
              <div className="mb-2">
                <RadioButton
                  name="tenantType"
                  value={selectedTenantType?.id}
                  options={tenantTypeItems}
                  onChange={handleTenantChange}
                ></RadioButton>
              </div>
              {selectedTenantType && selectedTenantType.id === "P" && (
                <>
                  <div className="mb-2">
                    <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                      Ime
                    </label>
                    <TextField
                      type="text"
                      name="firstName"
                      placeholder="Molimo Vas da unesete ime"
                      onChange={handleTextFieldChange}
                      value={formValues.firstName}
                      error={errors.firstName}
                      height="big"
                      required
                    ></TextField>
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                      Prezime
                    </label>
                    <TextField
                      type="text"
                      name="lastName"
                      placeholder="Molimo Vas da unesete prezime"
                      onChange={handleTextFieldChange}
                      value={formValues.lastName}
                      error={errors.lastName}
                      height="big"
                      required
                    ></TextField>
                  </div>
                </>
              )}
              {selectedTenantType && selectedTenantType.id === "C" && (
                <>
                  <div className="mb-2">
                    <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                      Naziv tvrtke
                    </label>
                    <TextField
                      type="text"
                      name="companyName"
                      placeholder="Molimo Vas da unesete naziv tvrtke"
                      onChange={handleTextFieldChange}
                      value={formValues.companyName}
                      error={errors.companyName}
                      height="big"
                      required
                    ></TextField>
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                      OIB
                    </label>
                    <TextField
                      type="text"
                      name="taxNumber"
                      placeholder="Molimo Vas da unesete oib tvrtke"
                      onChange={handleTextFieldChange}
                      value={formValues.taxNumber}
                      error={errors.taxNumber}
                      height="big"
                      required
                    ></TextField>
                  </div>
                </>
              )}
              <div className="mb-2">
                <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                  Email
                </label>
                <TextField
                  type="email"
                  name="email"
                  placeholder="Molimo Vas da unesete email"
                  onChange={handleTextFieldChange}
                  value={formValues.email}
                  error={errors.email}
                  height="big"
                  required
                ></TextField>
              </div>
              <div className="mb-2">
                <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                  Lozinka
                </label>
                <PasswordField
                  name="password"
                  placeholder="Molimo Vas da unesete lozinku"
                  value={formValues.password}
                  onChange={handlePasswordChange}
                  required
                  showRequirements
                  error={errors.password}
                  isPasswordValid={isPasswordValid}
                  height="big"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                  Ponovljena lozinka
                </label>
                <PasswordField
                  name="confirmPassword"
                  placeholder="Molimo Vas da ponovite lozinku"
                  value={formValues.confirmPassword}
                  onChange={handleTextFieldChange}
                  required
                  error={errors.confirmPassword}
                  height="big"
                />
              </div>
              <div className="mb-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="full"
                  className="font-medium py-4 !text-base rounded-md"
                >
                  Potvrdi
                </Button>
              </div>
              <p className="text-md text-center">
                Već imate račun?{" "}
                <Link to={LoginPage.route} className="text-blue-500">
                  Prijavite se
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};

export default RegistrationForm;
